// 产品表单弹窗组件
import React, { useState, useMemo } from 'react';
import { X, Save, Upload, Image as ImageIcon } from 'lucide-react';
import type { Product, ProductCategory } from '@/poster/types';
import type { ProductSeries } from '../hooks/useSeriesManager';
import { uploadProductImage } from '@/poster/services/product-service';

interface ProductFormModalProps {
  product: Product | null;
  seriesList: ProductSeries[];
  defaultCategory?: ProductCategory;
  defaultSeries?: string;
  onClose: () => void;
  onSave: (data: Partial<Product>) => Promise<void>;
}

export function ProductFormModal({
  product,
  seriesList,
  defaultCategory,
  defaultSeries,
  onClose,
  onSave,
}: ProductFormModalProps) {
  // 根据当前分类筛选可用的系列
  const [currentCategory, setCurrentCategory] = useState<ProductCategory>(
    product?.category || defaultCategory || 'product'
  );
  const availableSeries = useMemo(() => {
    return seriesList.filter(s => s.category === currentCategory);
  }, [seriesList, currentCategory]);
  
  const [formData, setFormData] = useState<Partial<Product>>({
    name: product?.name || '',
    name_en: product?.name_en || '',
    category: product?.category || defaultCategory || 'product',
    series_name: product?.series_name || defaultSeries || '',
    image_url: product?.image_url || '',
    pitch_range: product?.pitch_range || '',
    size_specs: product?.size_specs || '',
    process_type: product?.process_type || '',
    features: product?.features || [],
    slogan: product?.slogan || '',
    is_featured: product?.is_featured || false,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [featuresInput, setFeaturesInput] = useState(
    product?.features?.join(', ') || ''
  );

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const { url, storageKey } = await uploadProductImage(file, product?.id);
      setFormData((prev) => ({
        ...prev,
        image_url: url,
        image_storage_key: storageKey,
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('图片上传失败');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const features = featuresInput
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);

      await onSave({
        ...formData,
        features,
      });
    } catch (error) {
      console.error('Error saving product:', error);
      alert('保存失败');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {product ? '编辑产品' : '添加产品'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                产品图片 (16:9)
              </label>
              <div className="flex items-start gap-4">
                <div className="w-40 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {formData.image_url ? (
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors">
                      <Upload className="w-4 h-4" />
                      {isUploading ? '上传中...' : '上传图片'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                  <p className="mt-2 text-xs text-gray-500">
                    建议尺寸: 1920×1080 或 16:9 比例
                  </p>
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  产品名称 *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="如: UTV SC"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  英文名称
                </label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name_en: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  产品分类 *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => {
                    const newCategory = e.target.value as ProductCategory;
                    setCurrentCategory(newCategory);
                    setFormData((prev) => ({
                      ...prev,
                      category: newCategory,
                      series_name: '', // 切换分类时清空系列
                    }));
                  }}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all_in_one">一体机</option>
                  <option value="product">产品</option>
                  <option value="module">模组</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  所属系列 *
                </label>
                <select
                  value={formData.series_name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, series_name: e.target.value }))
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">选择系列...</option>
                  {availableSeries.map((series) => (
                    <option key={series.id} value={series.name}>
                      {series.name}
                    </option>
                  ))}
                </select>
                {availableSeries.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">
                    该分类下暂无系列，请先在系列管理中创建
                  </p>
                )}
              </div>
            </div>

            {/* Technical Specs */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  间距范围
                </label>
                <input
                  type="text"
                  value={formData.pitch_range}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, pitch_range: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="如: P0.6-P2.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  尺寸规格
                </label>
                <input
                  type="text"
                  value={formData.size_specs}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, size_specs: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder='如: 135"/162"'
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  工艺类型
                </label>
                <input
                  type="text"
                  value={formData.process_type}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, process_type: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="如: COB, SMD, MIP"
                />
              </div>
            </div>

            {/* Features */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                产品特点 (逗号分隔)
              </label>
              <input
                type="text"
                value={featuresInput}
                onChange={(e) => setFeaturesInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="如: High Refresh, Ultra-thin, Easy Install"
              />
            </div>

            {/* Slogan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slogan
              </label>
              <input
                type="text"
                value={formData.slogan}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, slogan: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="产品宣传语"
              />
            </div>

            {/* Featured */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_featured"
                checked={formData.is_featured}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, is_featured: e.target.checked }))
                }
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_featured" className="text-sm text-gray-700">
                设为推荐产品
              </label>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || !formData.name}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSaving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
